import { ChangeEvent, ReactNode, useState } from "react";
import { Container } from "reactstrap";
import { FormikErrors, FormikTouched } from "formik";
import ActionButton from "Components/atoms/buttons/action-button";
import ApproveButton from "Components/atoms/buttons/approve-button";
import PrimaryButton from "Components/atoms/buttons/primary-button";
import RejectButton from "Components/atoms/buttons/reject-button";
import Breadcrumb from "Components/organisms/breadcrumb";
import LoadingState from "Components/organisms/loading-state";
import DocumentCard from "Components/molecule/cards/document-card";
import Modal from "Components/molecule/modal";
import RejectDocumentForm from "Components/organisms/forms/reject-document-form";
import ApproveOnBehalfForm from "Components/organisms/forms/approve-on-behalf-form";
import RejectDocumentFormFields from "Components/organisms/forms/reject-document-form/types";
import FormProps from "data/forms";
import Doc from "models/document";
import Employee from "models/employee";
import { GEN_DOC_STATUS } from "models/gen-doc-status";
import ConsultantAgreementForm from "Components/organisms/forms/consultant-agreement-form";
import { SelectOption } from "Components/molecule/inputs/select-input";
import AreYouSureButtons from "Components/molecule/are-you-sure-buttons";
import { CheckCircleIcon, CloseCircleIcon, DownloadIcon, HelpIcon } from "Components/atoms/icons";

export interface ConsultantAgreementFormValues {
    companyRepresentative: string;
    consultantRepresentative: string;
    termEndDate: string;
    duties: string;
    certifications: string[];
    location: string;
    siteAddress: string;
    schedule: string;
    descriptionTitle: string;
    hourlyRate: string;
    maxDailyRate: string;
    vehicleReimbursement: string;
    reimbursementType: string;
    vehicleRate: string;
    loaApplicable: string;
    loaRate: string;
    travelRequired: string;
    otherExpenses: string;
}


interface ConsultantAgreementTemplateProps extends FormProps<RejectDocumentFormFields> {
    doc?: Doc;
    processingDocument: boolean;
    viewingDocument: boolean;
    showRejectModal: boolean;
    showCandidateRejectModal: boolean;
    candidateRejectForm: FormProps<RejectDocumentFormFields>;
    showApproveOnBehalf: boolean;
    showApproveModal: boolean
    generateDocError: string | null;
    docStatus?: GEN_DOC_STATUS;
    approvers?: Employee[];
    approvalBlockedReason?: string | null;
    jobTitles: SelectOption[];
    certifications: SelectOption[],
    companyRepresentatives: SelectOption[]
    recruitFormSection: ReactNode
    editable: boolean;
    caValues: ConsultantAgreementFormValues;
    caErrors: FormikErrors<ConsultantAgreementFormValues>;
    caTouched: FormikTouched<ConsultantAgreementFormValues>;
    caHandleChange: (e: ChangeEvent<any>) => void;
    caHandleBlur: (e: React.FocusEvent<any>) => void;
    caSetFieldValue: (field: string, value: any) => void;
    handleDraftDocument?: () => void;
    handleSubmitDocument?: () => void;
    handleDownloadDocument?: () => void;
    handleApproveDocument?: () => void;
    handleShowRejectModal?: () => void;
    handleHideRejectModal?: () => void;
    handleShowCandidateRejectModal?: () => void;
    handleHideCandidateRejectModal?: () => void;
    handleOpenHelpDocument?: () => void;
    handleApproveOnBehalf?: (employees: string[]) => Promise<void>;
    handleShowApproveOnBehalf?: () => void;
    handleHideApproveOnBehalf?: () => void;
    handleShowApproveModal?: () => any,
    handleHideApproveModal?: () => any,
    handleBack: () => void;
    handleViewDocument: (doc: Doc) => void;
}

const ConsultantAgreementTemplate = ({
    doc,
    processingDocument,
    viewingDocument,
    showRejectModal,
    showCandidateRejectModal,
    candidateRejectForm,
    showApproveOnBehalf,
    showApproveModal,
    generateDocError,
    docStatus,
    approvers,
    approvalBlockedReason,
    jobTitles,
    certifications,
    editable,
    caValues,
    caErrors,
    caTouched,
    caHandleChange, caHandleBlur, caSetFieldValue, companyRepresentatives, recruitFormSection,
    handleDraftDocument, handleSubmitDocument, handleDownloadDocument, handleApproveDocument,
    handleShowRejectModal, handleHideRejectModal, handleOpenHelpDocument,
    handleShowCandidateRejectModal, handleHideCandidateRejectModal,
    handleApproveOnBehalf, handleShowApproveOnBehalf, handleHideApproveOnBehalf,
    handleBack, handleViewDocument,
    handleHideApproveModal,
    handleShowApproveModal,
    ...rejectProps
}: ConsultantAgreementTemplateProps) => {

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumb
                    title="Human Resources"
                    breadcrumbItem="Consultant Agreement"
                    actions={<>
                        {handleOpenHelpDocument && <ActionButton onClick={handleOpenHelpDocument}>Help <HelpIcon /></ActionButton>}
                        <ActionButton onClick={handleBack}>Back</ActionButton>
                    </>}
                />
                {doc ? (
                    <>
                        <div className="section-container">
                            {recruitFormSection}
                            <ConsultantAgreementForm
                                companyRepresentatives={companyRepresentatives}
                                jobTitles={jobTitles}
                                certifications={certifications}
                                caValues={caValues}
                                caErrors={caErrors}
                                caTouched={caTouched}
                                editable={editable}
                                caHandleChange={caHandleChange}
                                caHandleBlur={caHandleBlur}
                                caSetFieldValue={caSetFieldValue}
                            />
                        </div>

                        <div className="document-and-actions-container section-container">
                            <DocumentCard document={doc} handleView={handleViewDocument} loading={viewingDocument} />
                            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                <div className="generate-documents-button-container">
                                    {handleDraftDocument && <PrimaryButton disabled={processingDocument} onClick={handleDraftDocument}>Save Draft</PrimaryButton>}
                                    {handleSubmitDocument && <PrimaryButton disabled={processingDocument} onClick={handleSubmitDocument}>Submit</PrimaryButton>}
                                    {handleDownloadDocument && <PrimaryButton disabled={processingDocument} onClick={handleDownloadDocument}>Download Agreement <DownloadIcon /></PrimaryButton>}
                                    {handleShowApproveModal && <ApproveButton disabled={processingDocument} onClick={handleShowApproveModal}>{processingDocument ? <>Approving... <CheckCircleIcon /></> : <>Approve <CheckCircleIcon /></>}</ApproveButton>}
                                    {handleShowRejectModal && !handleShowApproveModal && <ApproveButton title={approvalBlockedReason ?? undefined} disabled={true} >Approve <CheckCircleIcon /></ApproveButton>}
                                    {handleShowRejectModal && <RejectButton disabled={processingDocument} onClick={handleShowRejectModal}>{processingDocument ? <>Rejecting... <CloseCircleIcon /></> : <>Reject <CloseCircleIcon /></>}</RejectButton>}
                                    {handleShowCandidateRejectModal && <RejectButton disabled={processingDocument} onClick={handleShowCandidateRejectModal}>{processingDocument ? <>Rejecting... <CloseCircleIcon /></> : <>Candidate Reject <CloseCircleIcon /></>}</RejectButton>}
                                    {handleShowApproveOnBehalf && <PrimaryButton style={{ backgroundColor: "var(--bs-blue)" }} disabled={processingDocument} onClick={handleShowApproveOnBehalf}>Approve On Behalf</PrimaryButton>}
                                </div>
                                {generateDocError && <span>{generateDocError}</span>}
                            </div>
                        </div>
                    </>
                ) : (
                    <LoadingState title="Loading Agreement" subtitle="Please wait while we fetch the agreement." />
                )}

                {handleHideRejectModal && (
                    <Modal isOpen={showRejectModal} onClose={handleHideRejectModal} title="Reject Document">
                        <RejectDocumentForm {...rejectProps} />
                    </Modal>
                )}

                {handleHideCandidateRejectModal && (
                    <Modal isOpen={showCandidateRejectModal} onClose={handleHideCandidateRejectModal} title="Reject Document By Candidate">
                        <RejectDocumentForm {...candidateRejectForm} />
                    </Modal>
                )}

                {
                    handleApproveDocument && handleHideApproveModal &&
                    <Modal
                        isOpen={showApproveModal}
                        onClose={handleHideApproveModal}
                        title={"Accept Document"}
                    >
                        <AreYouSureButtons
                            handleAccept={handleApproveDocument}
                            handleDecline={handleHideApproveModal}
                            acceptText={"Approve Document"}
                            declineText={"Cancel"}
                        />
                    </Modal>
                }
                {handleHideApproveOnBehalf && handleApproveOnBehalf && approvers && (
                    <Modal isOpen={showApproveOnBehalf} onClose={handleHideApproveOnBehalf} title="Approve On Behalf">
                        <ApproveOnBehalfForm employees={approvers} onSubmit={handleApproveOnBehalf} onCancel={handleHideApproveOnBehalf} />
                    </Modal>
                )}
            </Container>
        </div>
    );
};

export default ConsultantAgreementTemplate;
